<template>
  <div v-if="visible" class="overlay-modal">
    <div class="modal-crear">
      <h4 class="modal-titulo">Modificar formulario</h4>

      <div v-if="cargando" class="info">Cargando formulario…</div>

      <template v-else>
        <div class="form-group">
          <label>Nombre del formulario</label>
          <input v-model="nombre" type="text" placeholder="Nombre del formulario" />
        </div>

        <div class="form-group">
          <label>Tipo de instrumento</label>
          <input :value="etiquetaHerramienta" type="text" disabled class="campo-disabled" />
        </div>

        <div class="form-group">
          <label>¿Cómo califica?</label>
          <select v-model="modalidad">
            <option value="por_pregunta">Cada pregunta califica su indicador</option>
            <option value="formulario_completo">El formulario completo califica un indicador</option>
          </select>
        </div>

        <div class="seccion">
          <div class="seccion-head">
            <h5>Preguntas</h5>
            <button type="button" class="btn btn-sm guardar" :disabled="mostrandoBorrador" @click="abrirBorrador">+ Pregunta</button>
          </div>

          <!-- Borrador para nueva pregunta -->
          <div v-if="mostrandoBorrador" class="borrador-card">
            <div class="pregunta-head">
              <strong>Nueva pregunta</strong>
              <button type="button" class="btn-quitar" @click="cerrarBorrador">✕</button>
            </div>
            <div class="form-group">
              <label>Enunciado</label>
              <input v-model="borrador.enunciado" type="text" placeholder="¿En cuánto calificaría…?" />
            </div>
            <div class="form-group">
              <label>Tipo de respuesta</label>
              <select v-model="borrador.tipo_respuesta">
                <option value="" disabled>— Seleccionar tipo —</option>
                <option v-for="t in tiposDisponibles" :key="t.valor" :value="t.valor">{{ t.etiqueta }}</option>
              </select>
            </div>
            <div v-if="requiereOpciones(borrador.tipo_respuesta)" class="form-group">
              <label>Opciones (una por línea, mínimo 2)</label>
              <textarea v-model="borrador.opcionesTexto" rows="3" placeholder="Totalmente de acuerdo&#10;De acuerdo&#10;En desacuerdo"></textarea>
            </div>
            <div class="borrador-botones">
              <button type="button" class="btn btn-sm guardar" :disabled="!borrador.enunciado.trim() || !borrador.tipo_respuesta" @click="confirmarBorrador">Agregar</button>
            </div>
          </div>

          <p v-if="!preguntasVivas.length && !mostrandoBorrador" class="info">No hay preguntas.</p>

          <div v-for="(p, i) in preguntasVivas" :key="p.id_pregunta ?? i" class="pregunta-card">
            <div class="pregunta-head">
              <strong>Pregunta {{ i + 1 }}</strong>
              <button type="button" class="btn-quitar" @click="quitarPregunta(i)">✕</button>
            </div>
            <div v-if="p.indicadorNombre" class="indicador-ref">
              <span class="ind-badge">Indicador</span>
              <span class="ind-nombre">{{ p.indicadorNombre }}</span>
            </div>
            <div class="form-group">
              <label>Enunciado</label>
              <input v-model="p.enunciado" type="text" />
            </div>
            <div class="form-group">
              <label>Tipo de respuesta</label>
              <select v-model="p.tipo_respuesta" :disabled="tiposDisponibles.length === 1">
                <option v-for="t in tiposDisponibles" :key="t.valor" :value="t.valor">{{ t.etiqueta }}</option>
              </select>
            </div>
            <div v-if="requiereOpciones(p.tipo_respuesta)" class="form-group">
              <label>Opciones (una por línea, mínimo 2)</label>
              <textarea v-model="p.opcionesTexto" rows="3"></textarea>
            </div>
          </div>
        </div>

        <p v-if="error" class="error">{{ error }}</p>
      </template>

      <div class="botones">
        <button class="btn salir" :disabled="guardando" @click="$emit('cerrar')">Cancelar</button>
        <button class="btn guardar" :disabled="guardando || cargando || !nombre.trim()" @click="guardar">
          {{ guardando ? 'Guardando…' : 'Guardar cambios' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Edición de un formulario de opinión en borrador.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, reactive, computed, watch } from 'vue'
import { useGestionInstrumento } from '@/store/GestionInstrumentos'
import { useGestionPreguntas } from '@/store/GestionPreguntas'
import { getCatalogoInstrumentosService } from '@/services/InstrumentosService'
import type { CatalogoInstrumento, ModalidadInstrumento } from '@/interfaces/Instrumetos'

interface PreguntaEditable {
  id_pregunta?: number
  enunciado: string
  tipo_respuesta: string
  opcionesTexto: string
  id_indicador?: number | null
  indicadorNombre?: string | null
  id_pregunta_origen?: number | null
  eliminada: boolean
}

const props = defineProps<{ visible: boolean; idInstrumento: number | null }>()

const emit = defineEmits<{
  (e: 'cerrar'): void
  (e: 'guardado', mensaje: string): void
}>()

const instrumentoStore = useGestionInstrumento()
const preguntaStore = useGestionPreguntas()

const TIPOS_CON_OPCIONES = ['opcion_unica', 'single_choice', 'multiple_choice', 'casillas']
const TIPOS_PREGUNTA = [
  { valor: 'opcion_unica', etiqueta: 'Opción única' },
  { valor: 'escala_likert', etiqueta: 'Escala Likert' },
  { valor: 'escala_lineal', etiqueta: 'Escala lineal' },
  { valor: 'multiple_choice', etiqueta: 'Casillas' },
  { valor: 'short_text', etiqueta: 'Respuesta corta' },
  { valor: 'long_text', etiqueta: 'Párrafo' }
]

const nombre = ref('')
const herramienta = ref('')
const modalidad = ref<ModalidadInstrumento>('por_pregunta')
const idGrupoInteres = ref<number | null>(null)
const preguntas = ref<PreguntaEditable[]>([])
const catalogo = ref<CatalogoInstrumento>({ herramientas: [], modalidades: [] })
const cargando = ref(false)
const guardando = ref(false)
const error = ref('')

const mostrandoBorrador = ref(false)
const borrador = reactive({ enunciado: '', tipo_respuesta: '', opcionesTexto: '' })

const herramientaActual = computed(() =>
  catalogo.value.herramientas.find(h => h.valor === herramienta.value)
)
const etiquetaHerramienta = computed(() => herramientaActual.value?.etiqueta ?? herramienta.value)
const tiposPreguntaPermitidos = computed(() => herramientaActual.value?.tipos_pregunta ?? [])
const tiposDisponibles = computed(() => {
  const permitidos = tiposPreguntaPermitidos.value
  if (!permitidos.length) return TIPOS_PREGUNTA
  return TIPOS_PREGUNTA.filter(t => permitidos.includes(t.valor))
})

const preguntasVivas = computed(() => preguntas.value.filter(p => !p.eliminada))

function requiereOpciones(tipo: string): boolean {
  return TIPOS_CON_OPCIONES.includes(tipo)
}

function opcionesDe(opcionesTexto: string): string[] {
  return opcionesTexto.split('\n').map(o => o.trim()).filter(Boolean)
}

function abrirBorrador() {
  borrador.enunciado = ''
  borrador.tipo_respuesta = tiposDisponibles.value.length === 1
    ? tiposDisponibles.value[0].valor
    : ''
  borrador.opcionesTexto = ''
  mostrandoBorrador.value = true
}

function confirmarBorrador() {
  preguntas.value.push({
    enunciado: borrador.enunciado,
    tipo_respuesta: borrador.tipo_respuesta,
    opcionesTexto: borrador.opcionesTexto,
    eliminada: false
  })
  mostrandoBorrador.value = false
}

function cerrarBorrador() {
  mostrandoBorrador.value = false
}

function quitarPregunta(i: number) {
  const p = preguntasVivas.value[i]
  if (!p) return
  if (p.id_pregunta) {
    p.eliminada = true
  } else {
    const idx = preguntas.value.indexOf(p)
    if (idx !== -1) preguntas.value.splice(idx, 1)
  }
}

watch(
  () => [props.visible, props.idInstrumento] as const,
  async ([visible, id]) => {
    if (!visible || !id) return
    cargando.value = true
    error.value = ''
    mostrandoBorrador.value = false
    try {
      const [inst, todasPreguntas, cat] = await Promise.all([
        instrumentoStore.getInstrumentoById(id),
        preguntaStore.getPreguntas({ id_instrumento: id }),
        getCatalogoInstrumentosService()
      ])
      catalogo.value = cat
      if (inst) {
        nombre.value = inst.descripcion ?? ''
        herramienta.value = inst.herramienta ?? ''
        modalidad.value = inst.modalidad ?? 'por_pregunta'
        // Grupo del instrumento: se usa al crear preguntas nuevas para que
        // el backend las asocie al Google Form correcto.
        idGrupoInteres.value =
          (inst as any).id_grupo_interes
          ?? inst.grupos_interes?.[0]?.id_grupo_interes
          ?? null
      }
      preguntas.value = (todasPreguntas ?? []).map((p: any) => {
        const ind = Array.isArray(p.indicadores) ? p.indicadores[0] : null
        return {
          id_pregunta:      p.id_pregunta,
          enunciado:        p.enunciado ?? p.pregunta ?? '',
          tipo_respuesta:   p.tipo_respuesta ?? p.tipo ?? '',
          opcionesTexto:    Array.isArray(p.opciones) ? p.opciones.join('\n') : '',
          id_indicador:     p.id_indicador ?? ind?.id_indicador ?? null,
          indicadorNombre:  ind?.enunciado ?? null,
          id_pregunta_origen: p.id_pregunta_origen ?? null,
          eliminada:        false
        }
      })
    } finally {
      cargando.value = false
    }
  },
  { immediate: true }
)

async function guardar() {
  if (!props.idInstrumento) return
  guardando.value = true
  error.value = ''
  try {
    await instrumentoStore.actualizarInstrumento(props.idInstrumento, {
      descripcion: nombre.value.trim(),
      modalidad: modalidad.value
    } as any)

    for (const p of preguntas.value.filter(p => p.eliminada && p.id_pregunta)) {
      await preguntaStore.eliminarPregunta(p.id_pregunta!)
    }

    for (const p of preguntas.value.filter(p => !p.eliminada && p.id_pregunta)) {
      await preguntaStore.actualizarPregunta(p.id_pregunta!, {
        enunciado:     p.enunciado.trim(),
        pregunta:      p.enunciado.trim(),
        tipo_respuesta: p.tipo_respuesta,
        tipo:          p.tipo_respuesta,
        opciones:      requiereOpciones(p.tipo_respuesta) ? opcionesDe(p.opcionesTexto) : []
      })
    }

    let orden = preguntas.value.filter(p => !p.eliminada && p.id_pregunta).length + 1
    for (const p of preguntas.value.filter(p => !p.eliminada && !p.id_pregunta)) {
      await preguntaStore.crearPregunta({
        enunciado:          p.enunciado.trim(),
        pregunta:           p.enunciado.trim(),
        tipo_respuesta:     p.tipo_respuesta,
        tipo:               p.tipo_respuesta,
        opciones:           requiereOpciones(p.tipo_respuesta) ? opcionesDe(p.opcionesTexto) : [],
        id_instrumento:     props.idInstrumento,
        id_indicador:       null,
        id_pregunta_origen: p.id_pregunta_origen ?? null,
        id_grupo_interes:   idGrupoInteres.value,
        orden:              orden++
      })
    }

    emit('guardado', 'Formulario actualizado correctamente.')
    emit('cerrar')
  } catch (e: any) {
    error.value = e?.message ?? 'Error al guardar el formulario.'
  } finally {
    guardando.value = false
  }
}
</script>

<style scoped>
.overlay-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.5rem;
}

.modal-crear {
  background: #fff;
  border-radius: 10px;
  padding: 1.4rem;
  width: 100%;
  max-width: 620px;
  max-height: 88vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.22);
  font-family: var(--font-family, inherit);
}

.modal-titulo {
  margin: 0 0 1rem;
  font-size: 1.1rem;
  font-weight: 700;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 0.8rem;
}

.form-group label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #444;
}

input, select, textarea {
  padding: 0.5rem 0.65rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.85rem;
}

.campo-disabled {
  background: #f5f5f5;
  color: #666;
  cursor: default;
}

.seccion-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem 0 0.6rem;
}

.seccion-head h5 {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
}

.pregunta-card {
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 0.7rem 0.8rem;
  margin-bottom: 0.6rem;
  background: #fdfdfd;
}

.pregunta-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.82rem;
}

.btn-quitar {
  border: none;
  background: none;
  color: #b00020;
  cursor: pointer;
  font-size: 0.9rem;
}

.indicador-ref {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  background: #e8f1f8;
  border: 1px solid #b8d0e8;
  border-radius: 6px;
  padding: 3px 10px;
  margin-bottom: 0.5rem;
  font-size: 0.76rem;
}

.ind-badge {
  font-weight: 700;
  color: #0c447c;
  white-space: nowrap;
}

.ind-nombre {
  color: #1e2a35;
}

.borrador-card {
  border: 2px dashed #0e69a0;
  border-radius: 8px;
  padding: 0.7rem 0.8rem;
  margin-bottom: 0.8rem;
  background: #f5faff;
}

.borrador-botones {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.6rem;
}

.info {
  font-size: 0.82rem;
  color: #888;
}

.error {
  margin-top: 0.6rem;
  padding: 0.5rem 0.7rem;
  background: #f8d7da;
  color: #721c24;
  border-radius: 5px;
  font-size: 0.8rem;
}

.botones {
  display: flex;
  justify-content: flex-end;
  gap: 0.7rem;
  margin-top: 1.1rem;
}

.btn {
  padding: 8px 20px;
  height: 38px;
  font-weight: 600;
  font-size: 0.83rem;
  font-family: inherit;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.btn-sm {
  height: auto;
  padding: 0.35rem 0.8rem;
  font-size: 0.78rem;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.salir {
  background: #fff;
  border: 1px solid #ccc;
}

.btn.guardar {
  background: #eea222;
  color: #000;
}

.btn.guardar:not(:disabled):hover {
  background: #0e69a0;
  color: #fff;
}
</style>
